import { Component, OnInit, Input, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, inject } from '@angular/core';
import * as moment from 'moment-timezone';
import { DatatableService } from '../datatable.service';
import { DatatableFilter } from '../datatable-filter';
import { DatatableColumn } from '../datatable-column';
import { UserInputOptionType } from '../../shared-models/user-input-option-type';
import { FilterDataType, FilterOperation, FilterOperations } from './filter-operations';
import { EntityIdFormatterService } from '../../utility/entity-id-formatter.service';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MaterialModule } from '../../material/material.module';
import { MatChipsModule } from '@angular/material/chips';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'app-datatable-filter',
  templateUrl: './datatable-filter.component.html',
  styleUrls: ['./datatable-filter.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  imports: [
    CommonModule,
    MatIconModule,
    MaterialModule,
    MatChipsModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatButtonModule,
    MatSelectModule,
    MatInput,
    MatCheckbox,
  ],
})
export class DatatableFilterComponent implements OnInit {
  private datatableService = inject(DatatableService);
  private filterOperations = inject(FilterOperations);
  private entityIdFormatter = inject(EntityIdFormatterService);

  @Input() columns: DatatableColumn[];
  @Input() defaultFilter: DatatableFilter[];
  @Input() resetAttributesAfterApply: boolean;
  @Input() filterByAttributes: DatatableFilter;
  @Output() filtersChanged: EventEmitter<DatatableFilter[]> = new EventEmitter();
  @Input() warningMessage: string;

  filters: DatatableFilter[] = [];
  columnToFilter: string;
  filterValue = '';
  showFilters = false;
  filteredColumns: boolean[] = [];
  filterableColumns: DatatableColumn[];
  attributeFilterCheckmarks: boolean[];
  invalidFilterMessage: string;

  filterOperator: FilterOperation;
  filterOperators: FilterOperation[];

  filterHint: string;
  filterPlaceholder: string;
  showSystemIdMask: boolean;
  filterValueMaxLength: number;

  filterCaller: string;
  lastFilterCaller: string;

  readonly SYS_ID_MASK = 'SS-00-0000-000';
  readonly SYS_ID_PLACEHOLDER = 'XX-00-0000-000';
  readonly SYS_ID_PATTERN = '^[A-Z]{2}-\\d{2}-\\d{4}-\\d{3}$';
  readonly SYS_ID_TYPE_PLACEHOLDER = 'XX';

  readonly TYPE_CODE_FILTER_REGEX = /^[a-zA-Z]{2}$/;
  readonly SYSTEM_ID_FILTER_REGEX = /^[a-zA-Z]{2}-?\d{2}-?\d{4}-?\d{3}$/;
  // This pattern will match single dates ("1/1/21", "01/01/2021"):
  readonly SINGLE_DATE_FILTER_REGEX = /^\d{1,2}\/\d{1,2}\/\d{2}(\d{2})?$/;
  // This pattern will match date ranges ("1/1/21 - 01/01/2021", "01/01/2021-1/1/21"):
  readonly DATE_RANGE_FILTER_REGEX = /^\d{1,2}?\/\d{1,2}?\/\d{2}(\d{2})?\s?-\s?\d{1,2}?\/\d{1,2}?\/\d{2}(\d{2})?$/;
  // We use moment to make sure the date is valid (i.e. 13/32/2021 isn't a valid date)
  readonly VALID_MOMENT_FORMATS = [
    'M/D/YYYY',
    'MM/D/YYYY',
    'M/DD/YYYY',
    'MM/DD/YYYY',
    'M/D/YY',
    'MM/D/YY',
    'M/DD/YY',
    'MM/DD/YY',
  ];

  
 

  

  ngOnInit() {
    this.filterableColumns = this.columns.filter(
      (col: DatatableColumn) => col.filterDataType !== FilterDataType.None,
    );
    this.addDefaultFilters();
    this.showFilters = !!this.filters?.length;
  }

  addDefaultFilters(): void {
    this.getActiveTab();
    this.lastFilterCaller = sessionStorage.getItem('lastFilterCaller');

    if (this.checkFilters()) {
      this.filters = JSON.parse(sessionStorage.getItem(this.filterCaller).toString());
    } else {
      this.defaultFilter?.forEach(filter =>
        this.datatableService.addFilter(
          this.filters,
          {
            property: filter.property,
            title:
              filter.title ||
              this.datatableService.getColumnInfo(this.columns, filter.property).title,
            value: filter.value,
            filterDatesInUtc: filter.filterDatesInUtc,
            operator: filter.operator ?? UserInputOptionType.Contains,
          },
          this.filteredColumns,
        ),
      );
    }
    //store filter data for later retrieval
    sessionStorage.setItem('lastFilterCaller', this.filterCaller);

    this.filtersChanged.emit(this.filters);
  }

  checkFilters() {
    if (this.lastFilterCaller === null) {
      return false;
    }
    if (this.lastFilterCaller === this.filterCaller) {
      return false;
    }
    if (sessionStorage.getItem(this.filterCaller) === null) {
      return false;
    }
    return true;
  }

  addFilters() {
    this.getActiveTab();

    let reloadTable = false;
    if (!this.isInvalidFilterOption()) {
      this.addFilter();
      reloadTable = true;
    }
    if (!this.isCheckboxListNullOrAllUnchecked(this.attributeFilterCheckmarks)) {
      this.addAttributeFilter();
      reloadTable = true;
    }

    //store filter data for later retrieval
    sessionStorage.setItem('lastFilterCaller', this.filterCaller);
    sessionStorage.setItem(this.filterCaller, JSON.stringify(this.filters));

    if (reloadTable) {
      this.filtersChanged.emit(this.filters);
    }
  }

  isInvalidFilterOption() {
    this.invalidFilterMessage = null;
    if (this.columnToFilter?.length && this.columns?.length) {
      if (!this.filterOperator) {
        return true;
      }
      if (
        this.filterOperator.value === UserInputOptionType.Empty ||
        this.filterOperator.value === UserInputOptionType.NotEmpty
      ) {
        this.filterValue = null;
        return false;
      } else if (!this.filterOperator.requiresUserValue) {
        return false;
      } else if (this.filterValue?.length) {
        const colInfo = this.datatableService.getColumnInfo(this.columns, this.columnToFilter);
        if (colInfo.filterDataType === FilterDataType.Date) {
          // First check if the Date matches the associated Regex
          if (
            !this.filterValue.match(
              this.filterOperator.value === UserInputOptionType.InRange
                ? this.DATE_RANGE_FILTER_REGEX
                : this.SINGLE_DATE_FILTER_REGEX,
            )
          ) {
            this.invalidFilterMessage = 'Invalid Date Search value';
            return true;
          }
          // Then check that each date (two if a range, one otherwise) with moment to make sure it's valid
          const dateWithoutSymbols = this.filterValue.split('-');
          dateWithoutSymbols.forEach(date => {
            if (!moment(date.trim(), this.VALID_MOMENT_FORMATS, true).isValid()) {
              this.invalidFilterMessage = 'Invalid Date Search value';
              return true;
            }
          });
        } else if (colInfo.filterDataType === FilterDataType.SystemId) {
          if (
            this.filterOperator === this.filterOperations.IS_SYSTEM_TYPE ||
            this.filterOperator === this.filterOperations.IS_NOT_SYSTEM_TYPE
          ) {
            if (!this.filterValue.match(this.TYPE_CODE_FILTER_REGEX)) {
              if (this.filterValue.length >= 11) {
                this.invalidFilterMessage = 'Invalid System Type Code';
              }
              return true;
            }
            return false;
          } else if (
            this.filterOperator === this.filterOperations.EQUALS ||
            this.filterOperator === this.filterOperations.DOES_NOT_EQUAL
          ) {
            if (!this.filterValue.match(this.SYSTEM_ID_FILTER_REGEX)) {
              if (this.filterValue.length >= 11) {
                this.invalidFilterMessage = 'Invalid System ID';
              }
              return true;
            }
            return false;
          }
        }
      }
    }
    // >= 2 for Contains/DoesNotContain only; everything else should be >= 1
    const minCharsRequired = [
      this.filterOperations.CONTAINS,
      this.filterOperations.DOES_NOT_CONTAIN,
    ].includes(this.filterOperator)
      ? 2
      : 1;
    return !(this.columnToFilter != null && this.filterValue?.length >= minCharsRequired);
  }

  setFilterOperators() {
    if (this.columnToFilter) {
      const colInfo = this.datatableService.getColumnInfo(this.columns, this.columnToFilter);

      if (!colInfo) {
        this.filterOperators = null;
        this.filterOperator = null;
      } else {
        this.filterOperations
          .getFilterOperations(
            colInfo.filterDataType,
            !!colInfo.filterSystemIdColumnNames,
            colInfo.filterBooleanShowEmpty,
            colInfo.filterReferenceSourceName,
          )
          .subscribe(filterOps => {
            this.filterOperators = filterOps;
            this.filterOperator = this.filterOperators?.[0];
            this.filterOperatorChanged();
          });
      }
    } else {
      this.filterOperators = null;
      this.filterOperator = null;
    }
    this.filterValue = null;
  }

  filterOperatorChanged = () => {
    this.filterHint = null;
    this.filterValueMaxLength = undefined;
    this.showSystemIdMask = false;
    if (this.columns?.length && this.columnToFilter) {
      const colInfo = this.datatableService.getColumnInfo(this.columns, this.columnToFilter);
      if (colInfo) {
        this.filterHint = this.filterOperations.getFilterOperationHint(
          colInfo.filterDataType,
          this.filterOperator,
        );
        this.filterPlaceholder = this.getFilterPlaceholder(
          colInfo.filterDataType,
          this.filterOperator,
        );
        this.showSystemIdMask =
          colInfo.filterDataType === FilterDataType.SystemId &&
          (this.filterOperator === this.filterOperations.EQUALS ||
            this.filterOperator === this.filterOperations.DOES_NOT_EQUAL);
        if (
          colInfo.filterDataType === FilterDataType.SystemId &&
          (this.filterOperator === this.filterOperations.IS_SYSTEM_TYPE ||
            this.filterOperator === this.filterOperations.IS_NOT_SYSTEM_TYPE)
        ) {
          this.filterValueMaxLength = 2;
        }
      }
    }
  };

  getFilterPlaceholder(filterDataType: FilterDataType, filterOperation: FilterOperation) {
    if (filterOperation && filterDataType) {
      if (
        filterDataType === FilterDataType.SystemId &&
        [this.filterOperations.IS_SYSTEM_TYPE, this.filterOperations.IS_NOT_SYSTEM_TYPE].includes(
          filterOperation,
        )
      ) {
        return this.SYS_ID_TYPE_PLACEHOLDER;
      } else if (
        filterDataType === FilterDataType.SystemId &&
        [this.filterOperations.EQUALS, this.filterOperations.DOES_NOT_EQUAL].includes(
          filterOperation,
        )
      ) {
        return this.SYS_ID_PLACEHOLDER;
      }
    }
    return null;
  }

  addFilter() {
    const colInfo = this.datatableService.getColumnInfo(this.columns, this.columnToFilter);
    if (colInfo.filterDataType === FilterDataType.SystemId) {
      if (
        this.filterOperator === this.filterOperations.EQUALS ||
        this.filterOperator === this.filterOperations.DOES_NOT_EQUAL
      ) {
        const id = this.filterValue.substring(3).split('-').join('');
        const typeCode = this.filterValue.substring(0, 2);
        const valueDisplay = this.entityIdFormatter.getFormattedId(id, typeCode);
        const filterOperator = this.filterOperator?.value;
        if (
          colInfo?.filterSystemIdColumnNames?.typeCode &&
          colInfo?.filterSystemIdColumnNames?.id
        ) {
          // We need to add both system ID and type code
          // DEV NOTE: This approach adds two filter objects, both with the same property (one is hidden).
          // This requires the assumption that a user can not add more than one filter per column themselves
          // (removing one requires removing others with the same columnName).
          this.addFilterInternal(
            colInfo,
            colInfo.filterSystemIdColumnNames.id,
            id,
            filterOperator,
            valueDisplay,
            null,
            false,
            colInfo.property,
          );
          this.addFilterInternal(
            colInfo,
            colInfo.filterSystemIdColumnNames.typeCode,
            typeCode,
            filterOperator,
            valueDisplay,
            null,
            true,
            colInfo.property,
          );
        } else if (colInfo?.filterSystemIdFixedTypeCode) {
          this.addFilterInternal(
            colInfo,
            this.columnToFilter,
            id,
            filterOperator,
            valueDisplay,
            null,
            false,
            colInfo.property,
          );
          this.addFilterInternal(
            colInfo,
            null,
            typeCode,
            filterOperator,
            valueDisplay,
            null,
            true,
            colInfo.property,
            colInfo.filterSystemIdFixedTypeCode,
          );
        } else {
          console.error(
            'A System ID column does not have either the filterSystemIdColumnNames or the filterSystemIdFixedTypeCode defined. Define the column name for the ID portion and the TypeCode portion in the filter definition.',
          );
        }
      } else if (
        this.filterOperator === this.filterOperations.IS_SYSTEM_TYPE ||
        this.filterOperator === this.filterOperations.IS_NOT_SYSTEM_TYPE
      ) {
        // Only columns with multiple System Types can have this option. If we're missing this info, that's an issue
        if (!colInfo?.filterSystemIdColumnNames?.typeCode) {
          console.error(
            'A System ID column does not have the columnNames defined. Define the column name for the ID portion and the TypeCode portion in the filter definition.',
          );
          return;
        }
        this.filterValue = this.filterValue.toUpperCase();
        this.addFilterInternal(
          colInfo,
          colInfo.filterSystemIdColumnNames.typeCode,
          this.filterValue,
          this.filterOperator?.value,
        );
      } else {
        this.addFilterInternal(
          colInfo,
          this.columnToFilter,
          this.filterValue,
          this.filterOperator?.value,
        );
      }
    } else if (colInfo.filterDataType === FilterDataType.DateTime) {
      if (this.filterOperator.value === UserInputOptionType.InRange) {
        const dates = this.filterValue.split('-');
        const date1 = moment(dates[0], 'MM/DD/YYYY', 'America/New_York')
          .startOf('day')
          .utc()
          .format('MM/DD/YYYY HH:mm:ss')
          .toString();
        const date2 = moment(dates[1], 'MM/DD/YYYY', 'America/New_York')
          .add(1, 'day')
          .startOf('day')
          .utc()
          .format('MM/DD/YYYY HH:mm:ss')
          .toString();
        this.addFilterInternal(
          colInfo,
          this.columnToFilter,
          `${date1}-${date2}`,
          this.filterOperator?.value,
          this.filterValue,
        );
      } else if (
        this.filterOperator.value === UserInputOptionType.Equals ||
        this.filterOperator.value === UserInputOptionType.NotInRange
      ) {
        const filterDate = this.filterValue;
        const date1 = moment(filterDate, 'MM/DD/YYYY', 'America/New_York')
          .startOf('day')
          .utc()
          .format('MM/DD/YYYY HH:mm:ss')
          .toString();
        const date2 = moment(filterDate, 'MM/DD/YYYY', 'America/New_York')
          .add(1, 'day')
          .startOf('day')
          .utc()
          .format('MM/DD/YYYY HH:mm:ss')
          .toString();
        this.addFilterInternal(
          colInfo,
          this.columnToFilter,
          `${date1}-${date2}`,
          this.filterOperator.value === UserInputOptionType.Equals
            ? UserInputOptionType.InRange
            : this.filterOperator?.value,
          this.filterValue,
          this.filterOperator?.name,
        );
      } else if (
        this.filterOperator.value === UserInputOptionType.GreaterThan ||
        this.filterOperator.value === UserInputOptionType.LessThanOrEqual
      ) {
        const filterDate = moment(this.filterValue, 'MM/DD/YYYY', 'America/New_York')
          .add(1, 'day')
          .startOf('day')
          .utc()
          .format('MM/DD/YYYY HH:mm:ss')
          .toString();
        this.addFilterInternal(
          colInfo,
          this.columnToFilter,
          `${filterDate}`,
          this.filterOperator.value === UserInputOptionType.GreaterThan
            ? UserInputOptionType.GreaterThanOrEqual
            : UserInputOptionType.LessThan,
          this.filterValue,
        );
      } else if (
        this.filterOperator.value === UserInputOptionType.LessThan ||
        this.filterOperator.value === UserInputOptionType.GreaterThanOrEqual
      ) {
        const filterDate = moment(this.filterValue, 'MM/DD/YYYY', 'America/New_York')
          .startOf('day')
          .utc()
          .format('MM/DD/YYYY HH:mm:ss')
          .toString();
        this.addFilterInternal(
          colInfo,
          this.columnToFilter,
          `${filterDate}`,
          this.filterOperator?.value,
          this.filterValue,
        );
      } else {
        this.addFilterInternal(
          colInfo,
          this.columnToFilter,
          moment(this.filterValue).startOf('day').utc().format('MM/DD/YYYY').toString(),
          this.filterOperator?.value,
          this.filterValue,
        );
      }
    } else if (
      colInfo.filterDataType === FilterDataType.Boolean &&
      this.filterOperator === this.filterOperations.EMPTY_BOOLEAN
    ) {
      this.addFilterInternal(
        colInfo,
        this.columnToFilter,
        'false',
        this.filterOperator?.value,
        '',
        'is empty',
      );
    } else {
      this.addFilterInternal(
        colInfo,
        this.columnToFilter,
        this.filterValue,
        this.filterOperator?.value,
      );
    }
  }

  private addFilterInternal(
    colInfo: DatatableColumn,
    columnName: string,
    filterValue: string,
    filterOperator: UserInputOptionType,
    overrideDisplayValue?: string,
    overrideDisplayedOperator?: string,
    isHidden?: boolean,
    filterGroup?: string,
    fixedValue?: string,
  ) {
    if (
      this.datatableService.addFilter(
        this.filters,
        {
          property: columnName,
          value: filterValue?.trim(),
          valueDisplay: overrideDisplayValue,
          title: colInfo.title,
          filterDatesInUtc: colInfo.filterDatesInUtc,
          operator: filterOperator,
          operatorDisplay: overrideDisplayedOperator,
          hideFromUser: isHidden,
          filterGroup: filterGroup,
          fixedValue: fixedValue,
        },
        this.filteredColumns,
      )
    ) {
      this.filterValue = '';
      this.columnToFilter = null;
      this.filterOperators = null;
      this.filterOperator = null;
    }
  }

  addAttributeFilter() {
    // remove tag filter if exists
    this.datatableService.removeFilter(this.filters, this.filterByAttributes, this.filteredColumns);

    this.filters.push({
      title: this.filterByAttributes.title,
      property: this.filterByAttributes.property,
      values: this.filterByAttributes.values.filter(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (value, i) => this.attributeFilterCheckmarks[i],
      ),
      operator: UserInputOptionType.Contains,
    });

    if (this.resetAttributesAfterApply) {
      this.initializeCheckmarks();
    }
  }

  setCheckboxValues(event: any) {
    // initialize list of checkboxes for tracking state
    if (
      this.filterByAttributes &&
      this.filterByAttributes.values &&
      !this.attributeFilterCheckmarks
    ) {
      this.initializeCheckmarks();
    }
    // toggle checkbox in state
    this.attributeFilterCheckmarks[event] = !this.attributeFilterCheckmarks[event];
  }

  removeFilter(filter: DatatableFilter) {
    if (filter.filterGroup) {
      this.datatableService.removeFilterByGroup(this.filters, filter, this.filteredColumns);
    } else {
      this.datatableService.removeFilter(this.filters, filter, this.filteredColumns);
    }

    //store filter data for later retrieval
    sessionStorage.setItem('lastFilterCaller', this.filterCaller);
    sessionStorage.setItem(this.filterCaller, JSON.stringify(this.filters));

    this.filtersChanged.emit(this.filters);
  }

  resetFilters() {
    this.filters = [];
    this.filteredColumns = [];
    this.filterOperator = null;
    this.filterOperators = null;
    this.addDefaultFilters();
    this.attributeFilterCheckmarks = this.initializeCheckmarks();

    //store filter data for later retrieval
    sessionStorage.setItem('lastFilterCaller', this.filterCaller);
    sessionStorage.setItem(this.filterCaller, JSON.stringify(this.filters));

    this.filtersChanged.emit(this.filters);
  }

  clearFilters() {
    this.filters = [];
    this.filteredColumns = [];
    this.filterOperator = null;
    this.filterOperators = null;
    this.columnToFilter = null;
    this.filterValue = null;
    this.attributeFilterCheckmarks = this.initializeCheckmarks();

    //store filter data for later retrieval
    sessionStorage.setItem('lastFilterCaller', this.filterCaller);
    sessionStorage.setItem(this.filterCaller, JSON.stringify(this.filters));

    this.filtersChanged.emit(this.filters);
  }

  toggleFilter() {
    this.showFilters = !this.showFilters;
  }

  getFilterDisplay = (filter: DatatableFilter): string => {
    const operatorDisplay =
      filter.operatorDisplay ?? this.filterOperations.getFilterOperationDisplay(filter.operator);
    const valueText = filter.valueDisplay ?? (filter.values || filter.value);
    if (valueText) {
      return `${operatorDisplay} '${valueText}'`;
    } else {
      return operatorDisplay;
    }
  };

  getActiveTab() {
    const activetab = document.getElementsByClassName(
      'mdc-tab--active',
    ) as HTMLCollectionOf<HTMLElement>;
    if (activetab.length > 0) {
      const first = activetab[0];
      this.filterCaller = first.innerText.replace("'", '').split(' ').join('');
    }
  }

  isCheckboxListNullOrAllUnchecked(array: boolean[]) {
    return !array || !array.some(x => x);
  }

  initializeCheckmarks = () => {
    if (this.filterByAttributes) {
      return (this.attributeFilterCheckmarks = this.filterByAttributes.values.map(x => !x));
    } else {
      return null;
    }
  };
}
